import { LARGE_SCREEN, MEDIUM_SCREEN } from '../../../../hooks/useViewport'
import { useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage } from 'react-intl'

// Components
import { Text } from '../../..'
import ExternalListingsButton from '../../../buyers/Button/ExternalListingsButton'
import { ListingsCarousel } from '../../ListingsCarousel'
import { IconMapMarkerFilled } from '../../Icon'

// Selectors
import { selectHomeLat, selectHomeLng } from '../../../../store/selectors/home'
import { selectHomeValue } from '../../../../store/selectors/home/metadata'
import {
  selectSimilarHomesListings,
  selectDisplayYlopoSpecificContent,
  selectListingsSourceIsHomebotListings,
} from '../../../../store/selectors/listings'
import { selectCustomerId } from '../../../../store/selectors/customerProfile'

// Hooks
import { useViewAllListingsUrl } from '../../../../hooks/listings'

// Constants
import {
  LISTINGS_SIMILAR_HOMES,
  listingsSearchCriteria,
} from '../../../../constants/listings'

// Actions
import { searchListingsForCarousel } from '../../../../actions/listings'

// Styles
import baseStyles from '../MarketSpotlight.module.scss'
import styles from './MarketSimilarHomes.module.scss'
import { loadYlopo } from '../../../../helpers/ylopo'

export const MarketSimilarHomes = () => {
  const dispatch = useDispatch()
  const homeLat = useSelector(selectHomeLat)
  const homeLng = useSelector(selectHomeLng)
  const homeValue = useSelector(selectHomeValue)
  const listings = useSelector(selectSimilarHomesListings)
  const minPriceRange =
    homeValue * listingsSearchCriteria.SIMILAR_HOME_MIN_PRICE_MULTIPLIER
  const maxPriceRange =
    homeValue * listingsSearchCriteria.SIMILAR_HOME_MAX_PRICE_MULTIPLIER

  const showYlopoListings = useSelector(selectDisplayYlopoSpecificContent)
  const showHomebotListings = useSelector(selectListingsSourceIsHomebotListings)
  const customerId = useSelector(selectCustomerId)

  const dataSearch = useMemo(
    () => ({
      circle: {
        lat: homeLat,
        lng: homeLng,
        radius: listingsSearchCriteria.SIMILAR_HOME_DISTANCE,
      },
      limit: listingsSearchCriteria.SIMILAR_HOME_LIMIT,
      minPrice: minPriceRange,
      maxPrice: maxPriceRange,
      orderBy: 'sourceCreationDate,desc',
      tag: LISTINGS_SIMILAR_HOMES,
    }),
    [homeLat, homeLng, minPriceRange, maxPriceRange]
  )

  useEffect(() => {
    if (showHomebotListings) {
      dispatch(searchListingsForCarousel(customerId, dataSearch))
    } else if (showYlopoListings) {
      loadYlopo(() => {
        if (window.YLOPO_WIDGETS.remount) {
          window.YLOPO_WIDGETS.remount({ force: true })
        }
      })
    }
  }, [showYlopoListings, showHomebotListings, customerId, dispatch, dataSearch])

  const listingsSearchUrl = useViewAllListingsUrl(dataSearch)

  return (
    <>
      {showYlopoListings && !showHomebotListings && (
        <div
          style={{ display: 'none' }}
          className='YLOPO_resultsWidget'
          data-search={JSON.stringify(dataSearch)}
        />
      )}
      {listings && listings.length > 2 && (
        <div className={styles.marketSimilarHomes} data-qa='marketSimilarHomes'>
          <Text size='l' variant='neutralCool' variantCount='900'>
            <FormattedMessage
              id='MarketSpotlight.marketSimilarHomes'
              defaultMessage='Similar homes for sale nearby'
            />
          </Text>
          <Text
            size='xs'
            variant='neutralCool'
            variantCount='700'
            element='div'
            className={baseStyles.pill}
          >
            <IconMapMarkerFilled className={styles.mapMarker} />
            <FormattedMessage
              id='MarketSpotlight.marketSimilarHomes.distance'
              defaultMessage='within {distance, number} {distance, plural, one {mile} other {miles}} of your home'
              values={{
                distance: listingsSearchCriteria.SIMILAR_HOME_DISTANCE,
              }}
            />
          </Text>
          <ListingsCarousel
            listings={listings}
            lastSlideUrl={listingsSearchUrl}
            responsive={[
              {
                breakpoint: LARGE_SCREEN,
                settings: {
                  slidesToShow: 2,
                  slidesToScroll: 2,
                },
              },
              {
                breakpoint: MEDIUM_SCREEN,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
                },
              },
            ]}
          />
          <ExternalListingsButton
            className={styles.listingsButton}
            listingsUrl={listingsSearchUrl}
          >
            <FormattedMessage
              id='MarketSpotlight.marketSimilarHomes.view.more.button'
              defaultMessage='See all nearby homes'
            />
          </ExternalListingsButton>
        </div>
      )}
    </>
  )
}
